import React, { MouseEventHandler, MutableRefObject, useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import './CommonTreeNode.less';
import { IActionLinkItem } from 'components/ActionLinks/ActionLinkModels';
import { ActionLinks } from 'components/ActionLinks/ActionLinks';
import { IconButton } from 'components/buttons';
import { Callout } from 'components/layout/Callout';
import { TreeNodeName } from './TreeNodeName';

export interface ITreeNode<T = undefined>
  extends Omit<
    React.HTMLProps<HTMLDivElement>,
    'onClick' | 'onMouseUp' | 'onMouseDown' | 'onMouseOver' | 'onMouseOut' | 'data' | 'children' | 'id'
  > {
  data?: T;
  isExpanded: boolean;
  isLoading?: boolean;
  isActive?: boolean;
  name: string;
  description?: string;
  icon?: React.ReactChild;
  actions?: ITreeNodeAction[];
  subActions?: IActionLinkItem[];
  childNodes?: ITreeNode<T>[];
  nodeId: string;
  hasChildren?: boolean;
  isSelectable?: boolean;
  alwaysLoadChildren?: boolean;
}

export interface ITreeNodeContentProps<T> extends ITreeNode<T> {
  onClick?: MouseEventHandler<HTMLDivElement>;
  onMouseUp?: MouseEventHandler<HTMLDivElement>;
  onMouseDown?: MouseEventHandler<HTMLDivElement>;
  onMouseOver?: MouseEventHandler<HTMLDivElement>;
  onMouseOut?: MouseEventHandler<HTMLDivElement>;
  onExpandNodeClick?: MouseEventHandler<HTMLButtonElement>;

  /**
   * TODO: When move fully move to state managed next parameters could be moved in useContext
   */
  searchString?: string;

  components?: {
    name?: React.ReactChild;
    title?: React.ReactChild;
  };
}

export interface ITreeNodeAction {
  element: React.ReactChild;
  visibility: TreeNodeActionVisiblity;
}

export enum TreeNodeActionVisiblity {
  allways,
  onHover,
}

function getChevronClassName(isExpanded: boolean, hasChildren: boolean) {
  return classNames('dp-common-tree-node__triangle', {
    'dp-common-tree-node__triangle--expanded': isExpanded,
    'dp-common-tree-node__triangle--no-children': !hasChildren,
  });
}

function getNodeClassName(isActive: boolean, className?: string) {
  return classNames(
    'dp-common-tree-node',
    {
      'dp-common-tree-node--active': isActive,
    },
    className
  );
}

function getTreeNodeActionClassName(visibility: TreeNodeActionVisiblity) {
  return classNames('dp-common-tree-node__action', {
    'dp-common-tree-node__action--visible-on-hover': visibility === TreeNodeActionVisiblity.onHover,
  });
}

function getTreeNodeSubActionClassName(isOpen: boolean) {
  return classNames('dp-common-tree-node__sub-action-icon', {
    'dp-common-tree-node__sub-action-icon--open': isOpen,
  });
}

/**
 * Common Tree node for most components in portal
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function TreeNodeContent<T>({
  name,
  description,
  icon,
  actions,
  subActions,
  searchString,
  onClick,
  onMouseDown,
  onMouseUp,
  onExpandNodeClick,
  components,
  isActive,
  isExpanded,
  isSelectable,
  childNodes,
  hasChildren,
  isLoading,
  nodeId,
  data,
  onMouseOver,
  onMouseOut,
  ...props
}: React.PropsWithChildren<ITreeNodeContentProps<T>>) {
  const handleChevronMouseEvent: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      ((event.target as HTMLElement)?.offsetParent as HTMLElement)?.focus();
      event.stopPropagation();
      onExpandNodeClick?.(event);
    },
    [onExpandNodeClick]
  );

  const buttonRef: MutableRefObject<HTMLButtonElement | null> = useRef(null);
  const [areSubActionsOpen, setAreSubActionsOpen] = useState(false);

  return (
    <div
      {...props}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      className={getNodeClassName(!!isActive, props.className)}
      title={name + (description ? ` ${description}` : '')}
    >
      <IconButton
        className="dp-common-tree-node__expand-button"
        aria-label="Expand Node"
        onClick={hasChildren ? handleChevronMouseEvent : undefined}
        iconClassName={getChevronClassName(isExpanded, !!(hasChildren && childNodes?.length !== 0))}
      />
      {!!icon && <div className="dp-common-tree-node__icon">{icon}</div>}
      {components?.title ?? (
        <div className="dp-common-tree-node__title">
          {components?.name ?? <TreeNodeName name={name} searchString={searchString} />}
          {description && (
            <span className="dp-common-tree-node__description" title={description}>
              {description}
            </span>
          )}
        </div>
      )}
      {(actions || subActions) && (
        <div className="dp-common-tree-node__actions">
          {actions &&
            actions.map((action, i) => (
              <div key={i} className={getTreeNodeActionClassName(action.visibility)}>
                {action.element}
              </div>
            ))}
          {subActions && (
            <>
              <IconButton
                ref={buttonRef}
                onClick={() => {
                  setAreSubActionsOpen(true);
                }}
                className="dp-common-tree-node__action"
                iconClassName={getTreeNodeSubActionClassName(areSubActionsOpen)}
              />
              <Callout
                isOpen={areSubActionsOpen}
                onClose={() => {
                  setAreSubActionsOpen(false);
                }}
                sourceRef={buttonRef}
                placement="right"
                showArrow
              >
                <div className="dp-common-tree-node__sub-actions">
                  <ActionLinks values={{ current: subActions }} />
                </div>
              </Callout>
            </>
          )}
        </div>
      )}
    </div>
  );
}
